/**
 * FlashBook drafts — the creator's list of books saved but not published yet.
 *
 * The records themselves live on the creator's PDS (`status: 'draft'`). The
 * studio reads that list from the server (`app.bsky.feed.getActorFlashbooks`),
 * because unfinished work belongs to the creator, not to whichever device they
 * happened to be holding when they stopped.
 *
 * This module holds the device-local index that sits alongside it: an instant
 * list before the network answers, and a record of what this device last saw.
 * Nothing here is authoritative — it is a cache, and the merge below says so.
 */
import {device} from '#/storage'

/**
 * A cover, reduced to what a list row can show.
 *
 * A row exists so a creator recognises their own unfinished book, and a title is
 * a poor way to do that: two drafts of "Photosynthesis revision" are told apart by
 * their artwork, not by reading. Deliberately just two fields — a row is 46 pixels
 * tall and anything more about the cover would be detail nobody can see at that
 * size. The full cover lives on the record for whoever opens it.
 */
export type FlashBookDraftCover = {
  /** Artwork to paint in the row, already a URL the device can fetch. */
  imageUri?: string
  /** The cover opens on a film, which a still cannot say on its own. */
  isVideo?: boolean
}

export type FlashBookDraftRef = {
  /** AT-URI of the app.bsky.feed.flashbook record. */
  uri: string
  title: string
  pageCount: number
  /** ISO timestamp of the last save, newest first in the list. */
  updatedAt: string
  /** Absent for a draft saved before this field existed, and for one with no art. */
  cover?: FlashBookDraftCover
}

/** Enough history to recover recent work without becoming a junk drawer. */
const MAX_DRAFTS = 20

/** Newest first, and never longer than the history we offer. */
function order(drafts: FlashBookDraftRef[]): FlashBookDraftRef[] {
  return [...drafts]
    .sort((a, b) => String(b.updatedAt).localeCompare(String(a.updatedAt)))
    .slice(0, MAX_DRAFTS)
}

/**
 * This device's index, in the shape a row renders from.
 *
 * The cover is normalised on the way *out*, and not merely trusted because the
 * studio writes the right shape today. This key is persistent storage: it
 * outlives app versions, and its rows are painted straight from what it holds,
 * so a value written by any writer — now or later — must not be able to reach a
 * row as a shape the tile cannot read. Normalising here also makes the two
 * sources symmetric: a server row is already normalised by
 * `flashBookDraftRefFromView`, and a device row is normalised by this.
 */
export function getFlashBookDrafts(): FlashBookDraftRef[] {
  const stored = device.get(['flashbookDrafts']) ?? []
  return stored.map(draft => {
    const cover = flashBookDraftCoverOf(draft.cover)
    const {cover: _dropped, ...rest} = draft
    return {...rest, ...(cover ? {cover} : {})}
  })
}

/** Adds (or refreshes) a draft and returns the new index. */
export function rememberFlashBookDraft(
  draft: FlashBookDraftRef,
): FlashBookDraftRef[] {
  const next = order([
    draft,
    ...getFlashBookDrafts().filter(d => d.uri !== draft.uri),
  ])
  device.set(['flashbookDrafts'], next)
  return next
}

/** Drops a draft from the index (published, discarded, or gone). */
export function forgetFlashBookDraft(uri: string): FlashBookDraftRef[] {
  const next = getFlashBookDrafts().filter(d => d.uri !== uri)
  device.set(['flashbookDrafts'], next)
  return next
}

/**
 * The part of an `app.bsky.feed.getActorFlashbooks` item this list needs.
 *
 * Declared structurally rather than imported, so the mapping stays testable in
 * a plain node environment and the studio is not coupled to generated types.
 */
export type FlashbookDraftView = {
  uri?: string
  title?: string
  pageCount?: number
  pages?: unknown[]
  indexedAt?: string
  /** The record's own cover, flattened alongside its other fields. */
  cover?: unknown
}

/** A non-empty string, or undefined — URLs arrive as `null` as often as absent. */
function url(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value : undefined
}

/**
 * The cover a row can show, read off whatever holds the fields.
 *
 * One function for two callers, because both are reading the *same* record: the
 * server view, where the cover arrives as untrusted JSON, and the save that just
 * uploaded it, where it is a `FlashBookCover` the creator has in hand. Two
 * mappings that look alike would be two that drift, and the drift would show up
 * as a row's artwork appearing on one path and not the other.
 *
 * The record keeps the artwork under `imageUri` whichever *kind* the cover is,
 * and a film may also carry its own poster in `videoThumbnailUri`. A still is the
 * better thumbnail when there is one, so it is preferred; falling back to the
 * artwork is what stops a film cover from having no picture at all.
 *
 * Read defensively because one of those callers is a network shape from a
 * service this app does not own: a `null` cover, a cover that is a string, or a
 * URL that is empty all have to end as "no artwork" rather than as a broken
 * image in the list.
 *
 * Both shapes are also read by *the way back*: the device index stores what this
 * returns, and returns it through here again on the way to a row, so the result
 * has to be a valid input to itself.
 */
export function flashBookDraftCoverOf(
  cover: unknown,
): FlashBookDraftCover | undefined {
  if (!cover || typeof cover !== 'object') return undefined
  const value = cover as {
    kind?: unknown
    imageUri?: unknown
    videoThumbnailUri?: unknown
    videoUri?: unknown
    /** Already-mapped, which is what the device index stores. */
    isVideo?: unknown
  }
  const imageUri = url(value.videoThumbnailUri) ?? url(value.imageUri)
  /*
   * Three ways to know it is a film, and all three are needed.
   *
   * `kind` is what the record says the cover is, `videoUri` is what it has —
   * either alone is enough, because a record written before one of them existed
   * still has the other. `isVideo` is the third: this same function runs over a
   * row it has *already mapped* whenever the device index is read, and that shape
   * carries neither `kind` nor `videoUri`. Without it the flag was dropped on the
   * way back out, so a film draft with no poster — the one case where the flag is
   * the only thing there is to show — came back looking exactly like a draft with
   * no cover at all, which is the one distinction the tile exists to draw.
   */
  const isVideo =
    value.isVideo === true || value.kind === 'video' || !!url(value.videoUri)
  if (!imageUri && !isVideo) return undefined
  return {...(imageUri ? {imageUri} : {}), ...(isVideo ? {isVideo} : {})}
}

/**
 * Maps a server view to a draft reference.
 *
 * `indexedAt` rather than the record's own `createdAt`, which is the honest
 * thing to show: re-saving a draft deliberately keeps the original `createdAt`,
 * so it would claim a two-week-old draft was "just now". `indexedAt` is when
 * this save became visible.
 */
export function flashBookDraftRefFromView(
  view: FlashbookDraftView,
): FlashBookDraftRef | null {
  if (!view.uri) return null
  const pageCount =
    typeof view.pageCount === 'number'
      ? view.pageCount
      : Array.isArray(view.pages)
        ? view.pages.length
        : 0
  const cover = flashBookDraftCoverOf(view.cover)
  return {
    uri: view.uri,
    title: view.title?.trim() || 'Untitled book',
    pageCount,
    updatedAt: view.indexedAt ?? '',
    // Spread rather than set, so a coverless draft is stored without the key at
    // all instead of with an `undefined` one the merge would carry forever.
    ...(cover ? {cover} : {}),
  }
}

/**
 * Merges the server's list with what this device remembers.
 *
 * The server wins every conflict — it is the only thing that knows a draft was
 * published or deleted somewhere else. A local entry the server does not list
 * is *kept* rather than pruned, deliberately: a save made a moment ago may not
 * have been indexed yet, and dropping it would hide the creator's work at the
 * exact moment they saved it. The cost of that choice is a stale row until
 * something touches it, which is the far cheaper mistake.
 */
export function mergeFlashBookDrafts(
  local: FlashBookDraftRef[],
  remote: FlashBookDraftRef[],
): FlashBookDraftRef[] {
  const byUri = new Map<string, FlashBookDraftRef>()
  for (const draft of local) byUri.set(draft.uri, draft)
  for (const draft of remote) byUri.set(draft.uri, draft)
  return order([...byUri.values()])
}

/**
 * Applies a merge and persists it, so the next cold start already has it.
 * Returns the new index for the caller's state.
 */
export function syncFlashBookDrafts(
  remote: FlashBookDraftRef[],
): FlashBookDraftRef[] {
  const next = mergeFlashBookDrafts(getFlashBookDrafts(), remote)
  device.set(['flashbookDrafts'], next)
  return next
}
